import { z } from "zod";
import { DocumentSchema, DocumentTypeSchema } from "./document";
import { OptionalPhoneSchema, UserIdNumberSchema } from "./user";
import { bituahProviderOptions, sexOptions } from "./utils";
import { ProfileSchema } from "./profile";
import { ServiceLevelSchema } from "./serviceLevel";
import { RequestDeclarationSchema } from "./declaration";

const request_types = ["new", "renew", "increase"] as const;
type RequestTypesLabels = {
	[K in (typeof request_types)[number]]: React.ReactNode;
};
const requestTypeSchema = z.enum(request_types, {
	errorMap: () => ({ message: "יש לבחור אפשרות" }),
});

const dosageTypeOptions = ["flowering", "oil"] as const;

const dosageTypeOptionsLabels: Record<(typeof dosageTypeOptions)[number], string> = {
	oil: "שמן קנאביס",
	flowering: "תפרחת קנאביס",
};

const personalInfoSchema = ProfileSchema.omit({
	id: true,
	documents: true,
	coinsBalance: true,
	joinLinkLastSentAt: true,
	// Admin-managed relationship status — not a user-facing form field. Must be
	// omitted or .required() below would make it a required field with no input,
	// breaking the multi-step form's "next" validation. See CLAUDE.md gotcha.
	status: true,
})
	.required()
	.extend({
		phone2: OptionalPhoneSchema,
		email: ProfileSchema._def.shape().email.optional(),
	});

const yes_no_question = z.boolean({
	errorMap: () => ({ message: "יש לבחור תשובה" }),
});

const transporters_schema = z
	.array(
		z.object({
			id: UserIdNumberSchema,
			name: z.string().min(2, {
				message: "יש להכניס שם תקני עבור המשנע",
			}),
		})
	)
	.optional();

const medicalQuestionsSchema = z.object({
	psychiatricTreatment: yes_no_question,
	addictionHistory: yes_no_question,
	highBloodSugar: yes_no_question,
	highBloodPressure: yes_no_question,
	hasHeartIssues: yes_no_question,
	hasWeaponLicense: yes_no_question,
	hasTransporters: yes_no_question,
});

const allowed_dosage_types = [
	"T22 / C4",
	"T18 / C3",
	"T12 / C2",
	"T1 / C1",
	"T1 / C28",
	"T8 / C8",
	"T12 / C12",
	"T3 / C12",
	"T3 / C18",
	"T1 / C22",
	"T1 / C24",
	"T0 / C26",
	"T5 / C5",
	"T15 / C3",
	"T10 / C10",
	"T10 / C2",
	"T5 / C10",
	"T3 / C15",
] as const;

const MIN_DOSAGE = 10;
const MAX_DOSAGE = 100;

const DosageSchema = z.object({
	type: z.string().max(50),
	amount: z.coerce
		.number()
		.max(MAX_DOSAGE, { message: `לא ניתן לאשר יותר מ-${MAX_DOSAGE} גרם` })
		.min(MIN_DOSAGE, {
			message: `מינון לא יכול להיות מתחת ל ${MIN_DOSAGE} גרם`,
		}),
	centralism: z.string().max(50), // TODO - use allowed dosages enum
});

type RequestTypeSchema = z.infer<typeof requestTypeSchema>;

type PersonalInfoSchema = z.infer<typeof personalInfoSchema>;

type MedicalQuestionsSchema = z.infer<typeof medicalQuestionsSchema>;

type SelectDosageSchema = z.infer<typeof DosageSchema>;

export const baseSchema = z
	.object({
		// request info
		id: z.string().min(1),
		userId: z.string(),
		brokerId: z.string().optional(),
		isRecommendationOnly: z.boolean().default(false).optional(),
		serviceLevel: ServiceLevelSchema.optional(),
		/** The patient declaration accepted when a client submitted the request. */
		declaration: RequestDeclarationSchema.optional(),
		documents: z.object({
			[DocumentTypeSchema.Values.ID]: DocumentSchema,
			[DocumentTypeSchema.Values.MEDICAL_SUMMARY]: DocumentSchema,
			[DocumentTypeSchema.Values.DOCTOR_RECOMMENDATION]: DocumentSchema,
			[DocumentTypeSchema.Values.MEDICATION_REPORT]: DocumentSchema.nullable().optional(),
			[DocumentTypeSchema.Values.CANNABIS_LICENSE]: DocumentSchema.nullable().optional(),
			[DocumentTypeSchema.Values.CONSUMPTION_TABLE]: DocumentSchema.nullable().optional(),
			[DocumentTypeSchema.Values.CUSTOM1]: DocumentSchema.nullable().optional(),
			[DocumentTypeSchema.Values.CUSTOM2]: DocumentSchema.nullable().optional(),
			[DocumentTypeSchema.Values.CUSTOM3]: DocumentSchema.nullable().optional(),
		}),
		documentsForClient: z
			.object({
				[DocumentTypeSchema.Values.CANNABIS_LICENSE]: DocumentSchema.nullable().optional(),
				[DocumentTypeSchema.Values.PRESCRIPTIONS]: DocumentSchema.nullable().optional(),
				[DocumentTypeSchema.Values.DOCTOR_RECOMMENDATION]: DocumentSchema.nullable().optional(),
			})
			.optional(),
		doctor: z
			.object({
				id: z.string(),
			})
			.optional(),
		paymentDetails: z
			.object({
				status: z.enum(["pending", "paid", "refund", "partially_paid", "cancelled"]),
				cost: z.number().optional(),
				payments: z
					.array(
						z.object({
							amount: z.number(),
							date: z.number(),
							/** Hyp transaction id — one payment is recorded once. */
							transactionId: z.string().optional(),
						})
					)
					.optional(),
				coinsAwarded: z.boolean().optional(),
			})
			.optional(),
		createdBy: z.enum(["client", "admin", "broker"]),
		status: z.enum([
			"draft", // user didn't complete flow
			"pending", // use complete flow and wait
			"change_required", // admin ask client to update missing/invalid data
			"change_required_broker", // admin ask broker to update missing/invalid data
			"pending_doctor", // request attached to doctor
			"approved_by_doctor",
			"rejected_by_doctor",
			"pending_external_documents", // admin request external documents
			// final status
			"completed", // admin mark as done
			"rejected", // admin mark as reject
		]),
		// for doctor only
		adminMessage: z.string().nonempty({ message: "שדה חובה" }).optional(),
		rejectMessageByDoctor: z.string().nonempty({ message: "שדה חובה" }).optional(),
		approveMessageByDoctor: z.string().optional(),
		adminMessageForClient: z.string().optional(),
		adminMessageForBroker: z.string().optional(),
		// dates
		// todo add activity (like chat)

		// doctor data
		doctorDocuments: z.array(DocumentSchema).optional(),

		// client input data
		clientDetails: personalInfoSchema,
		medicalQuestionnaire: medicalQuestionsSchema,
		reasonType: z.enum([
			"oncologyChemotherapy",
			"oncology",
			"gastroCrohns",
			"gastroColitis",
			"chronicPain",
			"infectiousDiseasesHiv",
			"neuroMultipleSclerosis",
			"neuroParkinsons",
			"neuroTourettes",
			"palliativeCare",
			"psychiatryPtsd",
			"neuroEpilepsyAdults",
			"neuroEpilepsyMinors",
			"autism",
			"dementia",
			"other",
		]),
		// dosage
		existsDosage: z.array(DosageSchema).max(3),
		dosages: z.array(DosageSchema).max(3),

		userComment: z.string().max(255, { message: "הערה יכולה להכיל עד 255 תווים" }).optional(),
		transporters: z
			.array(
				z.object({
					fullName: z.string().min(1, { message: "שם המשנע נדרש" }),
					idNumber: UserIdNumberSchema,
					document: DocumentSchema,
				})
			)
			.optional(),
		createdAt: z.number(),
		updatedAt: z.number().optional(),
		completedAt: z.number().optional(),
	});

const dynamicSchema = z.discriminatedUnion("requestType", [
	z.object({
		requestType: z.literal("new"),
		documents: z.object({
			[DocumentTypeSchema.Values.MEDICATION_REPORT]: DocumentSchema,
		}),
	}),
	z.object({
		requestType: z.literal("renew"),
		documents: z.object({}),
	}),
	z.object({
		requestType: z.literal("increase"),
		documents: z.object({
			[DocumentTypeSchema.Values.MEDICATION_REPORT]: DocumentSchema,
			[DocumentTypeSchema.Values.CONSUMPTION_TABLE]: DocumentSchema.optional(),
		}),
	}),
]);

export const RequestSchema = z.intersection(baseSchema, dynamicSchema);

export type TRequest = z.infer<typeof RequestSchema>;

export {
	personalInfoSchema,
	medicalQuestionsSchema,
	transporters_schema,
	requestTypeSchema,
	allowed_dosage_types,
	request_types,
	MIN_DOSAGE,
	MAX_DOSAGE,
	bituahProviderOptions,
	sexOptions,
	dosageTypeOptions,
	dosageTypeOptionsLabels,
};
export type {
	RequestTypesLabels,
	RequestTypeSchema,
	PersonalInfoSchema,
	MedicalQuestionsSchema,
	SelectDosageSchema,
};
